import { useEffect, useMemo, useRef, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { useSite } from "@/context/SiteContext";
import api from "@/lib/api";
import { Radio, PackageCheck, Gauge, Target, UserCheck, Boxes, Activity, Clock, AlertTriangle, Trophy, Timer, DoorOpen, ClipboardList, FileDown, Database, Loader2, Footprints, ChevronRight, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";

const TEAL = "#0f766e";
const KIND = { PICK: { c: "#DB2777", l: "PICK" }, MOVE: { c: "#0ea5e9", l: "MOVE" }, TASK: { c: "#f59e0b", l: "TASK" } };
const ago = (iso) => { if (!iso) return ""; const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000); if (s < 60) return `${Math.floor(s)}s`; if (s < 3600) return `${Math.floor(s / 60)}m`; if (s < 86400) return `${Math.floor(s / 3600)}h`; return `${Math.floor(s / 86400)}d`; };

export default function WarehouseMonitor() {
  const { activeSite, setActiveSite } = useSite();
  const site = activeSite && activeSite !== "GROUP" ? activeSite : "UK";
  const [act, setAct] = useState(null);
  const [stats, setStats] = useState([]);
  const [fleet, setFleet] = useState(null);
  const [waves, setWaves] = useState([]);
  const [pickers, setPickers] = useState([]);
  const [dock, setDock] = useState(null);
  const [shiftDate, setShiftDate] = useState(new Date().toISOString().slice(0, 10));
  const [shift, setShift] = useState(null);
  const [trend, setTrend] = useState(null);
  const [compare, setCompare] = useState([]);
  const [bins, setBins] = useState(null);
  const [sel, setSel] = useState({});
  const [nowMs, setNowMs] = useState(Date.now());
  const [seeding, setSeeding] = useState(false);
  const [detail, setDetail] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const shiftRef = useRef(null);
  const timer = useRef(null);
  const prevLate = useRef(new Set());
  const accAlert = useRef(new Set());
  const hopAlert = useRef(new Set());
  const inited = useRef(false);

  const loadAll = async () => {
    const [a, s, f, w, p, dk, bs] = await Promise.all([
      api.get(`/warehouse-activity?site=${site}`), api.get(`/picker-stats?site=${site}`),
      api.get(`/pod/fleet?site=${site}`), api.get(`/pick-waves?site=${site}`), api.get(`/pickers`), api.get(`/dock-schedule?site=${site}`), api.get(`/bin-status?site=${site}`)]);
    setAct(a.data); setStats(s.data); setFleet(f.data); setWaves(w.data); setPickers(p.data); setDock(dk.data); setBins(bs.data);
    const lateNow = new Set(f.data.vans.filter((v) => v.late).map((v) => v.route_no));
    if (inited.current) {
      lateNow.forEach((rn) => { if (!prevLate.current.has(rn)) toast.error(`Van ${rn} is now running LATE`); });
      s.data.forEach((pk) => {
        if (pk.lines > 0 && pk.accuracy < 85 && !accAlert.current.has(pk.picker_email)) { toast.warning(`${pk.picker_name}'s pick accuracy dropped to ${pk.accuracy}%`); accAlert.current.add(pk.picker_email); }
        if (pk.accuracy >= 85) accAlert.current.delete(pk.picker_email);
        const hopShare = pk.lines > 0 ? (pk.long_hops || 0) / pk.lines : 0;
        if ((pk.long_hops || 0) >= 40 && hopShare > 0.3 && !hopAlert.current.has(pk.picker_email)) { toast.warning(`${pk.picker_name} has ${pk.long_hops} long hops (>3m) — consider rebalancing the floor`); hopAlert.current.add(pk.picker_email); }
        if (hopShare <= 0.3) hopAlert.current.delete(pk.picker_email);
      });
    }
    prevLate.current = lateNow; inited.current = true;
  };
  useEffect(() => {
    inited.current = false; loadAll();
    timer.current = setInterval(loadAll, 5000);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(timer.current); clearInterval(tick); };
    // eslint-disable-next-line
  }, [site]);

  useEffect(() => {
    api.get(`/shift-summary?site=${site}&date=${shiftDate}`).then((r) => setShift(r.data)).catch(() => {});
    api.get(`/shift-summary?site=GROUP&date=${shiftDate}`).then((r) => setCompare(r.data.sites)).catch(() => {});
  }, [site, shiftDate]);

  useEffect(() => {
    api.get(`/shift-trend?site=${site}&days=7`).then((r) => setTrend(r.data.points)).catch(() => {});
  }, [site]);

  const assign = async (wave) => {
    const email = sel[wave.id]; if (!email) { toast.error("Pick a picker first"); return; }
    const p = pickers.find((x) => x.email === email);
    try { await api.post(`/pick-waves/${wave.id}/assign`, { picker_email: email, picker_name: p?.name || email }); toast.success(`${wave.wave_no} → ${p?.name || email}`); loadAll(); }
    catch { toast.error("Assign failed"); }
  };

  const openDay = (date) => {
    if (!date) return;
    setShiftDate(date);
    toast.info(`Showing ${date.slice(5)} shift summary`);
    setTimeout(() => shiftRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const openPicker = async (email) => {
    setPickerOpen(true); setDetail(null);
    try {
      const { data } = await api.get(`/picker-detail?email=${encodeURIComponent(email)}&site=${site}`);
      setDetail(data);
    } catch { toast.error("Could not load picker detail"); setPickerOpen(false); }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post(`/pod/demo-monitor?force=true`);
      toast.success(`Seeded ${data.pick_events} pick events across ${data.sites.length} sites`);
      await loadAll();
      const [sh, tr, cmp] = await Promise.all([
        api.get(`/shift-summary?site=${site}&date=${shiftDate}`),
        api.get(`/shift-trend?site=${site}&days=7`),
        api.get(`/shift-summary?site=GROUP&date=${shiftDate}`),
      ]);
      setShift(sh.data); setTrend(tr.data.points); setCompare(cmp.data.sites);
    } catch {
      toast.error("Seed failed");
    } finally { setSeeding(false); }
  };

  const lateVans = useMemo(() => (fleet?.vans || []).filter((v) => v.late).length, [fleet]);
  if (!act || !fleet) return <Loader label="Connecting to warehouse floor…" />;

  const openWaves = waves.filter((w) => w.status === "OPEN");

  return (
    <div data-testid="wh-monitor">
      <PageHeader title="Warehouse Live Monitor" subtitle="Real-time control tower — pick-wave assignment, picker productivity, dock scheduling and a live activity feed. Auto-refreshing every 5s." icon={Radio} accent={TEAL}>
        <button data-testid="wh-seed-btn" onClick={seedDemo} disabled={seeding} className="inline-flex items-center gap-1.5 rounded-md border border-[#e5e9f0] bg-white px-3 py-1.5 text-xs font-semibold text-[#0f766e] hover:bg-[#f0fdfa] disabled:opacity-60">
          {seeding ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />} {seeding ? "Seeding…" : "Seed demo week"}
        </button>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> LIVE</span>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testId="wh-kpi-waves" label="Open Waves" value={act.open_waves} accent="#DB2777" />
        <KpiCard testId="wh-kpi-ready" label="Ready to Load" value={act.ready_to_load} accent="#F59E0B" />
        <KpiCard testId="wh-kpi-vans" label="Active Vans" value={fleet.vans.length} accent="#0284C7" />
        <KpiCard testId="wh-kpi-late" label="Late Vans" value={lateVans} accent={lateVans ? "#EF4444" : "#10B981"} />
        <KpiCard testId="wh-kpi-pickers" label="Pickers" value={stats.filter((s) => s.lines > 0).length} accent={TEAL} />
      </div>

      {compare && compare.length > 1 && (
        <div data-testid="wh-compare" className="mb-6 rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Target size={15} /> Site Comparison — today's throughput &amp; on-time % <span className="ml-1 font-normal normal-case text-[#89919a]">· click a site to open its live view</span></h3>
          <div style={{ width: "100%", height: 220 }} data-testid="wh-compare-chart">
            <ResponsiveContainer>
              <ComposedChart data={compare} margin={{ top: 6, right: 8, left: -18, bottom: 0 }} style={{ cursor: "pointer" }} onClick={(e) => { const s = e?.activePayload?.[0]?.payload?.site_id; if (s) { setActiveSite(s); toast.info(`Switched to ${s} live view`); window.scrollTo({ top: 0, behavior: "smooth" }); } }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                <XAxis dataKey="site_id" tick={{ fontSize: 11, fill: "#6a6d70" }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#6a6d70" }} allowDecimals={false} />
                <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#6a6d70" }} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar yAxisId="l" dataKey="pick_lines" name="Pick lines" fill="#DB2777" radius={[3, 3, 0, 0]} barSize={26} />
                <Bar yAxisId="l" dataKey="delivered" name="Delivered" fill="#0284C7" radius={[3, 3, 0, 0]} barSize={26} />
                <Line yAxisId="r" type="monotone" dataKey="on_time_pct" name="On-time %" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {shift && (
        <div data-testid="wh-shift" ref={shiftRef} className="mb-6 rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><ClipboardList size={15} /> End-of-Day Shift Summary</h3>
            <input data-testid="wh-shift-date" type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)} className="rounded-md border border-[#e5e9f0] bg-white px-2 py-1 text-xs" />
            <div className="ml-auto flex items-center gap-3 text-xs">
              <span className="text-[#6a6d70]">On-time <b className={shift.totals.on_time_pct >= 95 ? "text-emerald-600" : shift.totals.on_time_pct >= 85 ? "text-amber-600" : "text-red-600"}>{shift.totals.on_time_pct}%</b></span>
              <span className="text-[#6a6d70]">SLA breaches <b className={shift.totals.sla_breaches ? "text-red-600" : "text-emerald-600"}>{shift.totals.sla_breaches}</b></span>
              <button data-testid="wh-shift-pdf" onClick={() => openPdf(`/shift-summary/pdf?site=${site}&date=${shiftDate}`)} className="inline-flex items-center gap-1 rounded-md bg-[#0f766e] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0b5f58]"><FileDown size={13} /> Export PDF</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
                <tr className="border-b border-[#eef1f5]"><th className="px-3 py-2">Site</th><th className="px-3 py-2 text-right">Pick Lines</th><th className="px-3 py-2 text-right">Lines/hr</th><th className="px-3 py-2 text-right">Accuracy</th><th className="px-3 py-2 text-right">Delivered</th><th className="px-3 py-2 text-right">Failed</th><th className="px-3 py-2 text-right">On-Time</th><th className="px-3 py-2 text-right">SLA Breaches</th></tr>
              </thead>
              <tbody>
                {shift.sites.map((r) => (
                  <tr key={r.site_id} data-testid={`wh-shift-${r.site_id}`} className="border-b border-[#f2f4f7] last:border-0">
                    <td className="px-3 py-2 font-semibold text-[#32363a]">{r.site_id}</td>
                    <td className="px-3 py-2 text-right">{r.pick_lines}</td>
                    <td className="px-3 py-2 text-right">{r.lines_per_hour}</td>
                    <td className={`px-3 py-2 text-right ${r.pick_accuracy >= 95 ? "text-emerald-600" : r.pick_accuracy >= 85 ? "text-amber-600" : "text-red-600"}`}>{r.pick_accuracy}%</td>
                    <td className="px-3 py-2 text-right">{r.delivered}</td>
                    <td className="px-3 py-2 text-right">{r.failed}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.on_time_pct >= 95 ? "text-emerald-600" : r.on_time_pct >= 85 ? "text-amber-600" : "text-red-600"}`}>{r.on_time_pct}%</td>
                    <td className={`px-3 py-2 text-right ${r.sla_breaches ? "font-semibold text-red-600" : "text-[#6a6d70]"}`}>{r.sla_breaches}</td>
                  </tr>
                ))}
                {shift.sites.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-[#6a6d70]">No activity recorded for this date.</td></tr>}
              </tbody>
            </table>
          </div>
          {shift.totals.failures && Object.keys(shift.totals.failures).length > 0 && (
            <div data-testid="wh-failures" className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#eef1f5] pt-3">
              <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]"><AlertTriangle size={13} className="text-red-500" /> Failed deliveries</span>
              {Object.entries(shift.totals.failures).sort((a, b) => b[1] - a[1]).map(([r, c]) => (
                <span key={r} data-testid={`wh-fail-reason`} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700"><b>{c}</b> {r}</span>
              ))}
            </div>
          )}
          {trend && trend.length > 0 && (
            <div className="mt-4 border-t border-[#eef1f5] pt-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]">7-Day Trend — throughput vs on-time % <span className="ml-1 font-normal normal-case text-[#89919a]">· click a day to open its shift summary</span></div>
              <div style={{ width: "100%", height: 200 }} data-testid="wh-trend">
                <ResponsiveContainer>
                  <ComposedChart data={trend} margin={{ top: 6, right: 8, left: -18, bottom: 0 }} style={{ cursor: "pointer" }} onClick={(e) => openDay(e?.activePayload?.[0]?.payload?.date)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6a6d70" }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#6a6d70" }} allowDecimals={false} />
                    <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#6a6d70" }} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar yAxisId="l" dataKey="pick_lines" name="Pick lines" fill="#DB2777" radius={[3, 3, 0, 0]} barSize={16} onClick={(d) => openDay(d?.date)} cursor="pointer" />
                    <Bar yAxisId="l" dataKey="delivered" name="Delivered" fill="#0284C7" radius={[3, 3, 0, 0]} barSize={16} onClick={(d) => openDay(d?.date)} cursor="pointer" />
                    <Line yAxisId="r" type="monotone" dataKey="on_time_pct" name="On-time %" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                    <Line yAxisId="l" type="monotone" dataKey="sla_breaches" name="SLA breaches" stroke="#ef4444" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          {/* Picker leaderboard */}
          {stats.filter((s) => s.lines > 0).length > 0 && (
            <div>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Trophy size={15} /> Picker Leaderboard — today</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {[...stats].filter((s) => s.lines > 0).sort((a, b) => b.lines_per_hour - a.lines_per_hour).slice(0, 3).map((p, i) => {
                  const medal = ["#eab308", "#94a3b8", "#b45309"][i];
                  return (
                    <div key={p.picker_email} data-testid={`wh-rank-${i + 1}`} className="flex items-center gap-3 rounded-xl border-2 bg-white p-4 shadow-sm" style={{ borderColor: medal }}>
                      <div className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: medal }}>{i + 1}</div>
                      <div className="flex-1"><div className="text-sm font-semibold text-[#32363a]">{p.picker_name}</div><div className="text-[11px] text-[#6a6d70]">{p.lines_per_hour}/hr · {p.accuracy}% acc · {p.lines} lines</div></div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Picker productivity */}
          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Gauge size={15} /> Picker Productivity</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {stats.map((p) => (
                <button key={p.picker_email} data-testid={`wh-picker-${p.picker_email}`} onClick={() => openPicker(p.picker_email)} className="group w-full rounded-xl border border-[#e5e9f0] bg-white p-4 text-left shadow-sm transition hover:border-[#DB2777]/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#DB2777]/30">
                  <div className="flex items-center gap-2"><div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#DB2777]/10 text-[#DB2777]"><UserCheck size={15} /></div><div className="flex-1 text-sm font-semibold text-[#32363a]">{p.picker_name}</div><ChevronRight size={15} className="text-[#c3c8cf] transition group-hover:text-[#DB2777]" /></div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-lg font-bold text-[#32363a]">{p.lines}</div><div className="text-[10px] uppercase text-[#89919a]">lines</div></div>
                    <div><div className="text-lg font-bold text-[#0284C7]">{p.lines_per_hour}</div><div className="text-[10px] uppercase text-[#89919a]">/ hour</div></div>
                    <div><div className={`text-lg font-bold ${p.accuracy >= 95 ? "text-emerald-600" : p.accuracy >= 85 ? "text-amber-600" : "text-red-600"}`}>{p.accuracy}%</div><div className="text-[10px] uppercase text-[#89919a]">accuracy</div></div>
                  </div>
                  {p.long_hops > 0 && (p.long_hops / Math.max(p.lines, 1) > 0.25 || p.long_hops >= 40) && (
                    <div data-testid={`wh-hopalert-${p.picker_email}`} className="mt-2 flex items-center justify-center gap-1 rounded-md bg-amber-50 py-1 text-[10px] font-semibold text-amber-700"><Footprints size={11} /> {p.long_hops} long hops &gt;3m · needs rebalancing</div>
                  )}
                  <div className="mt-2 text-center text-[10px] text-[#89919a]"><Clock size={9} className="inline" /> {p.last_active ? `active ${ago(p.last_active)} ago` : "no activity yet"} · <span className="text-[#DB2777]">view stats</span></div>
                </button>
              ))}
              {stats.length === 0 && <div className="text-xs text-[#6a6d70]">No picker activity yet.</div>}
            </div>
          </div>

          {/* Wave assignment */}
          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Boxes size={15} /> Pick-Wave Assignment</h3>
            <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
              {openWaves.length === 0 && <div className="p-5 text-center text-xs text-[#6a6d70]">No open waves. Build waves from Outbound Deliveries.</div>}
              {openWaves.map((w) => (
                <div key={w.id} data-testid={`wh-wave-${w.wave_no}`} className="flex flex-wrap items-center gap-2 border-b border-[#f2f4f7] px-4 py-2.5 last:border-0">
                  <span className="font-mono text-sm font-semibold text-[#DB2777]">{w.wave_no}</span>
                  <span className="text-xs text-[#6a6d70]">{w.open}/{w.total} tasks · {w.site_id}</span>
                  <button data-testid={`wh-picklist-${w.wave_no}`} onClick={() => openPdf(`/print/pick-list/${w.id}.pdf`)} title="Print pick list (A4)"
                    className="inline-flex items-center gap-1 rounded-md border border-[#e5e9f0] px-2 py-0.5 text-[11px] text-[#6a6d70] hover:border-[#DB2777] hover:text-[#DB2777]"><Printer size={12} /> Pick List</button>
                  {w.sla_due && (() => {
                    const ms = new Date(w.sla_due).getTime() - nowMs;
                    const risk = ms < 20 * 60000;
                    const am = Math.floor(Math.abs(ms) / 60000);
                    const as = Math.floor((Math.abs(ms) % 60000) / 1000);
                    return <span data-testid={`wh-sla-${w.wave_no}`} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${ms < 0 ? "bg-red-600 text-white" : risk ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}><Timer size={10} />{ms < 0 ? `OVERDUE ${am}m` : `SLA ${am}m ${String(as).padStart(2, "0")}s`}</span>;
                  })()}
                  {w.assigned_name ? <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><UserCheck size={11} /> {w.assigned_name}</span> : (
                    <div className="ml-auto flex items-center gap-1.5">
                      <select data-testid={`wh-wave-select-${w.wave_no}`} value={sel[w.id] || ""} onChange={(e) => setSel((s) => ({ ...s, [w.id]: e.target.value }))} className="rounded-md border border-[#e5e9f0] bg-white px-2 py-1 text-xs">
                        <option value="">Assign picker…</option>
                        {pickers.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
                      </select>
                      <button data-testid={`wh-wave-assign-${w.wave_no}`} onClick={() => assign(w)} className="rounded-md bg-[#0f766e] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#0b5f58]">Assign</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dock schedule + live activity feed */}
        <div className="space-y-6">
          {dock && dock.schedule && (
            <div data-testid="wh-dock">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><DoorOpen size={15} /> Dock & Departure Schedule</h3>
              <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
                {dock.schedule.length === 0 && <div className="p-4 text-center text-xs text-[#6a6d70]">Nothing ready to load right now.</div>}
                {dock.schedule.map((d) => (
                  <div key={d.slot} data-testid={`wh-dock-${d.slot}`} className="flex items-center gap-2 border-b border-[#f2f4f7] px-3 py-2 text-xs last:border-0">
                    <span className="flex h-6 w-6 items-center justify-center rounded bg-[#0f766e] text-[10px] font-bold text-white">{d.slot}</span>
                    <div className="flex-1"><span className="font-semibold text-[#32363a]">{d.bay}</span> · <span className="font-mono text-[#DB2777]">{d.wave_no}</span><div className="text-[10px] text-[#6a6d70]">{d.drops} drops · {d.weight}kg</div></div>
                    <span className="rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[11px] text-[#32363a]">dep {new Date(d.depart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {bins && bins.zones && bins.zones.length > 0 && (
            <div data-testid="wh-bins">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Boxes size={15} /> Warehouse Storage Status</h3>
              <div className="rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between text-xs">
                  <span className="text-[#6a6d70]">{bins.occupied}/{bins.total_bins} bins occupied</span>
                  <span className="font-semibold text-[#0f766e]">{bins.utilization}% utilised</span>
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="wh-bin-heatmap">
                  {bins.zones.map((z) => {
                    const u = z.utilization;
                    const bg = u > 90 ? "#dc2626" : u > 75 ? "#f97316" : u > 55 ? "#f59e0b" : u > 30 ? "#10b981" : "#86efac";
                    return (
                      <div key={z.zone} data-testid={`wh-bin-${z.zone}`} title={`${z.zone} · ${z.occupied}/${z.bins} bins · avg fill ${z.avg_fill}%`} className="rounded-lg p-3 text-white shadow-sm transition hover:scale-[1.02]" style={{ background: bg }}>
                        <div className="flex items-center justify-between"><span className="font-mono text-xs font-bold">{z.zone}</span><span className="text-lg font-extrabold">{u}%</span></div>
                        <div className="mt-0.5 text-[10px] opacity-90">{z.occupied}/{z.bins} bins</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-2 text-[10px] text-[#89919a]"><span className="inline-block h-2 w-3 rounded-sm" style={{ background: "#86efac" }} /> low <span className="inline-block h-2 w-3 rounded-sm" style={{ background: "#f59e0b" }} /> busy <span className="inline-block h-2 w-3 rounded-sm" style={{ background: "#dc2626" }} /> near-full</div>
              </div>
            </div>
          )}
          <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Activity size={15} /> Live Activity Feed</h3>
          <div data-testid="wh-feed" className="max-h-[820px] space-y-2 overflow-y-auto rounded-xl border border-[#e5e9f0] bg-white p-3 shadow-sm">
            {act.feed.length === 0 && <div className="py-8 text-center text-xs text-[#6a6d70]">Waiting for floor activity…</div>}
            {act.feed.map((f, i) => {
              const k = KIND[f.kind] || { c: "#64748b", l: f.kind };
              return (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-[#f2f4f7] px-3 py-2">
                  <span className="mt-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold text-white" style={{ background: k.c }}>{k.l}</span>
                  <div className="flex-1">
                    <div className="text-xs text-[#32363a]">{f.text}</div>
                    <div className="text-[10px] text-[#89919a]">{f.who} · {f.site_id} · {ago(f.at)} ago {f.ok === false && <AlertTriangle size={10} className="inline text-amber-500" />}</div>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </div>
      </div>

      <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) { setPickerOpen(false); setDetail(null); } }}>
        <DialogContent data-testid="picker-detail-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#DB2777]/10 text-[#DB2777]"><UserCheck size={16} /></div>
              {detail ? detail.picker_name : "Loading…"} <span className="text-xs font-normal text-[#89919a]">· picker performance</span>
            </DialogTitle>
          </DialogHeader>
          {!detail ? (
            <div className="flex items-center justify-center py-16 text-sm text-[#6a6d70]"><Loader2 size={18} className="mr-2 animate-spin" /> Loading picker stats…</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {[
                  ["Lines", detail.lines, "#32363a"],
                  ["Lines / hr", detail.lines_per_hour, "#0284C7"],
                  ["Accuracy", `${detail.accuracy}%`, detail.accuracy >= 95 ? "#059669" : detail.accuracy >= 85 ? "#d97706" : "#dc2626"],
                  ["Hops", detail.hops, "#32363a"],
                  ["Avg hop", `${detail.avg_hop_seconds}s`, "#32363a"],
                  ["Long hops >3m", detail.long_hops, detail.long_hops > 0 ? "#dc2626" : "#059669"],
                  ["Active hrs", detail.active_hours, "#32363a"],
                ].map(([label, val, color]) => (
                  <div key={label} className="rounded-lg border border-[#e5e9f0] bg-white p-2.5 text-center">
                    <div className="text-lg font-bold" style={{ color }}>{val}</div>
                    <div className="text-[10px] uppercase text-[#89919a]">{label}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]"><Footprints size={12} className="mr-1 inline" /> 7-day pick volume</div>
                <div style={{ width: "100%", height: 150 }} data-testid="picker-detail-trend">
                  <ResponsiveContainer>
                    <ComposedChart data={detail.per_day} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6a6d70" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#6a6d70" }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="lines" name="Pick lines" fill="#DB2777" radius={[3, 3, 0, 0]} barSize={18} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]">Pick zones</div>
                  <div className="space-y-1.5">
                    {detail.zones.map((z) => (
                      <div key={z.zone}>
                        <div className="flex justify-between text-[11px]"><span className="text-[#32363a]">{z.zone}</span><span className="text-[#6a6d70]">{z.lines}</span></div>
                        <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-[#eef1f5]"><div className="h-full rounded-full bg-[#DB2777]" style={{ width: `${detail.lines ? Math.round(z.lines / detail.lines * 100) : 0}%` }} /></div>
                      </div>
                    ))}
                    {detail.zones.length === 0 && <div className="text-[11px] text-[#89919a]">No zone data.</div>}
                  </div>
                </div>
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]">Recent picks</div>
                  <div className="max-h-[220px] overflow-y-auto rounded-lg border border-[#e5e9f0]">
                    <table className="w-full text-[11px]">
                      <tbody>
                        {detail.recent.map((r, i) => (
                          <tr key={i} className="border-b border-[#f2f4f7] last:border-0">
                            <td className="whitespace-nowrap px-2 py-1 text-[#6a6d70]">{r.at ? new Date(r.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</td>
                            <td className="whitespace-nowrap px-2 py-1 font-mono text-[#0a6ed1]">{r.delivery_no || ""}{r.order_no ? <span className="ml-1 text-[10px] text-[#89919a]">· {r.order_no}</span> : ""}</td>
                            <td className="px-2 py-1 text-[#32363a]">{r.material_name}</td>
                            <td className="px-2 py-1 text-right">{r.qty}</td>
                            <td className="whitespace-nowrap px-2 py-1 font-mono text-[#89919a]">{r.bin || r.zone || ""}</td>
                            <td className="px-2 py-1 text-center">{r.first_try ? <span className="text-emerald-600">✓</span> : <span className="text-amber-600">retry</span>}</td>
                          </tr>
                        ))}
                        {detail.recent.length === 0 && <tr><td className="px-2 py-4 text-center text-[#89919a]">No recent picks.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
